import { useState, useCallback, useEffect, useRef } from 'react';
import * as Cesium from 'cesium';
import { usePickTerrainPosition, useConstraintCheck } from './common/useCesiumInteraction';
import { useCustomDataSource } from './common/useCustomDataSource';
import { disableCameraControls, enableCameraControls } from '../utils/cesiumHelpers';
import { generateId } from '../utils/idGeneration';
import { useVisibilityControl } from './useVisibilityControl';
import { createLabelPickerEntity } from '../utils/cesiumEntityFactory';
const generateLabelId = () => generateId('label');
const screenPositionForEntity = (viewer, entity) => {
    var _a;
    if (!viewer || viewer.isDestroyed() || !entity)
        return null;
    const pos = (_a = entity.position) === null || _a === void 0 ? void 0 : _a.getValue(Cesium.JulianDate.now());
    if (!pos)
        return null;
    const screen = Cesium.SceneTransforms.worldToWindowCoordinates(viewer.scene, pos);
    if (!screen)
        return null;
    return { x: screen.x, y: screen.y };
};
export const useLabels = ({ viewer, isActive, projectId, constraintPolygon, defaultStyle, savedLabels, resolveServerFolderId, canEditSavedLabel, onFirstCommit }) => {
    const [labels, setLabels] = useState(new Map());
    const [editingLabel, setEditingLabel] = useState(null);
    const [selectedLabel, setSelectedLabel] = useState(null);
    const [labelCounter, setLabelCounter] = useState(1);
    const [dragging, setDragging] = useState(null);
    const [itemOpacities, setItemOpacities] = useState(new Map());
    const dataSourceRef = useCustomDataSource(viewer, 'tool-labels');
    const labelsRef = useRef(labels);
    const draggingRef = useRef(null);
    const editingRef = useRef(null);
    const defaultStyleRef = useRef(defaultStyle);
    const pickTerrainPositionRef = useRef(() => null);
    const isPositionInConstraintRef = useRef(() => true);
    // When the inline editor commits via blur, the blur fires synchronously
    // inside the same click event that propagates to the canvas. By the
    // time `handleMapClick` runs, `editingRef.current` is already null —
    // without a guard, that click is treated as a fresh placement, and
    // the user gets an unwanted second label. We stamp the commit time
    // here and ignore clicks for a short window.
    const lastCommitAtRef = useRef(0);
    useEffect(() => { labelsRef.current = labels; }, [labels]);
    useEffect(() => { draggingRef.current = dragging; }, [dragging]);
    useEffect(() => { editingRef.current = editingLabel; }, [editingLabel]);
    useEffect(() => { defaultStyleRef.current = defaultStyle; }, [defaultStyle]);
    const savedLabelsRef = useRef(savedLabels);
    useEffect(() => { savedLabelsRef.current = savedLabels; }, [savedLabels]);
    const resolveServerFolderIdRef = useRef(resolveServerFolderId);
    useEffect(() => { resolveServerFolderIdRef.current = resolveServerFolderId; }, [resolveServerFolderId]);
    const canEditSavedLabelRef = useRef(canEditSavedLabel);
    useEffect(() => { canEditSavedLabelRef.current = canEditSavedLabel; }, [canEditSavedLabel]);
    const onFirstCommitRef = useRef(onFirstCommit);
    useEffect(() => { onFirstCommitRef.current = onFirstCommit; }, [onFirstCommit]);
    const getLabelEntities = useCallback((l) => {
        return [l.entity];
    }, []);
    const { isVisible, setIsVisible, hideAll, showAll, toggleItemVisibility, isItemVisible } = useVisibilityControl(labels, getLabelEntities);
    const pickTerrainPosition = usePickTerrainPosition(viewer);
    const isPositionInConstraint = useConstraintCheck(constraintPolygon);
    useEffect(() => { pickTerrainPositionRef.current = pickTerrainPosition; }, [pickTerrainPosition]);
    useEffect(() => { isPositionInConstraintRef.current = isPositionInConstraint; }, [isPositionInConstraint]);
    const prevProjectIdRef = useRef(null);
    useEffect(() => {
        if (prevProjectIdRef.current !== null && prevProjectIdRef.current !== projectId) {
            if (dataSourceRef.current) {
                dataSourceRef.current.entities.removeAll();
            }
            setLabels(new Map());
            setEditingLabel(null);
            setSelectedLabel(null);
            setLabelCounter(1);
        }
        prevProjectIdRef.current = projectId;
    }, [projectId]);
    // While the inline editor is open we re-compute the editor's screen
    // position every frame so it stays anchored to the label as the camera
    // moves. Same trick the overlay layer uses for the visible text.
    useEffect(() => {
        if (!viewer || viewer.isDestroyed() || !editingLabel)
            return;
        const label = labelsRef.current.get(editingLabel.labelId);
        if (!label)
            return;
        const remove = viewer.scene.postRender.addEventListener(() => {
            const screen = screenPositionForEntity(viewer, label.entity);
            if (!screen)
                return;
            setEditingLabel(prev => {
                if (!prev || prev.labelId !== editingLabel.labelId)
                    return prev;
                if (Math.abs(prev.screenPosition.x - screen.x) < 0.5 && Math.abs(prev.screenPosition.y - screen.y) < 0.5) {
                    return prev;
                }
                return { ...prev, screenPosition: screen };
            });
        });
        return remove;
    }, [viewer, editingLabel === null || editingLabel === void 0 ? void 0 : editingLabel.labelId]);
    const placeLabelAtScreen = useCallback((screenPos) => {
        const position = pickTerrainPosition(screenPos);
        if (!position)
            return null;
        if (!isPositionInConstraint(position))
            return null;
        if (!dataSourceRef.current)
            return null;
        const id = generateLabelId();
        const name = `Label ${labelCounter}`;
        const s = defaultStyleRef.current;
        const entity = createLabelPickerEntity(dataSourceRef.current, position);
        const referenceDistance = viewer && !viewer.isDestroyed()
            ? Cesium.Cartesian3.distance(viewer.camera.positionWC, position)
            : undefined;
        const newLabel = {
            id,
            name,
            position,
            text: '',
            color: s.color,
            size: s.size,
            stroke: s.stroke,
            bold: s.bold,
            italic: s.italic,
            background: s.background,
            cornerRadius: s.cornerRadius,
            backgroundBorder: s.backgroundBorder,
            fontFamily: s.fontFamily,
            projectId: projectId || undefined,
            entity,
            referenceDistance
        };
        setLabels(prev => {
            const next = new Map(prev);
            next.set(id, newLabel);
            return next;
        });
        setLabelCounter(prev => prev + 1);
        setEditingLabel({
            labelId: id,
            screenPosition: { x: screenPos.x, y: screenPos.y }
        });
        return id;
    }, [pickTerrainPosition, isPositionInConstraint, labelCounter, projectId]);
    // Pull a saved label into the editable Map so it can be styled,
    // dragged, and re-saved through the normal flow. The saved label's
    // own Cesium entity is left alone here — MapViewer hides it (via the
    // hiddenSavedLabelIds set derived from this Map) so picking remains
    // unambiguous while the user has the hydrated copy open.
    const hydrateSavedLabel = useCallback((saved, folderId, screenPosition, mode) => {
        if (!dataSourceRef.current)
            return null;
        if (canEditSavedLabelRef.current && !canEditSavedLabelRef.current(saved.id))
            return null;
        const id = saved.id;
        const existing = labelsRef.current.get(id);
        if (existing && existing.serverArtifactId === saved.id) {
            // Already hydrated — just (re)open the requested view.
            if (mode === 'edit') {
                setEditingLabel({ labelId: id, screenPosition });
            }
            else {
                setSelectedLabel({ labelId: id, screenPosition });
            }
            return id;
        }
        const entity = createLabelPickerEntity(dataSourceRef.current, saved.position);
        const hydrated = {
            ...saved,
            projectId: projectId || saved.projectId,
            entity,
            serverArtifactId: saved.id,
            serverFolderId: folderId
        };
        setLabels(prev => {
            const next = new Map(prev);
            next.set(id, hydrated);
            return next;
        });
        if (mode === 'edit') {
            setEditingLabel({ labelId: id, screenPosition });
        }
        else {
            setSelectedLabel({ labelId: id, screenPosition });
        }
        return id;
    }, [projectId]);
    const checkExistingLabelClick = useCallback((event) => {
        if (!viewer || viewer.isDestroyed())
            return false;
        const picked = viewer.scene.pick(event.position);
        if (!(picked === null || picked === void 0 ? void 0 : picked.id))
            return false;
        const screen = { x: event.position.x, y: event.position.y };
        for (const [labelId, label] of labels.entries()) {
            if (picked.id === label.entity) {
                setSelectedLabel({ labelId, screenPosition: screen });
                return true;
            }
        }
        // Fall through to saved labels rendered by useServerArtifactRenderer.
        for (const [savedId, savedLabel] of savedLabelsRef.current.entries()) {
            if (picked.id === savedLabel.entity) {
                hydrateSavedLabel(savedLabel, resolveServerFolderIdRef.current(savedId), screen, 'select');
                return true;
            }
        }
        return false;
    }, [viewer, labels, hydrateSavedLabel]);
    const handleMapMouseDown = useCallback((event) => {
        if (!viewer || viewer.isDestroyed())
            return false;
        const picked = viewer.scene.pick(event.position);
        if (!(picked === null || picked === void 0 ? void 0 : picked.id))
            return false;
        for (const [id, label] of labels.entries()) {
            if (picked.id === label.entity) {
                setDragging({ labelId: id });
                disableCameraControls(viewer);
                return true;
            }
        }
        return false;
    }, [viewer, labels]);
    const handleMapMouseUp = useCallback(() => {
        if (dragging && viewer && !viewer.isDestroyed()) {
            enableCameraControls(viewer);
            setDragging(null);
            return true;
        }
        return false;
    }, [dragging, viewer]);
    const handleMapClick = useCallback((event) => {
        if (!isActive)
            return false;
        if (dragging)
            return false;
        if (editingRef.current) {
            // While the editor is open, clicking elsewhere counts as a commit
            // via the editor's onBlur. Don't drop a new label underneath.
            return true;
        }
        // The blur fires inside the same click that reaches us here, so by
        // the time editingRef has cleared we're still inside that gesture.
        // Swallow the click if it happens within a tiny window of the most
        // recent commit/cancel; otherwise the user gets an unintended
        // double-label every time they click away from the editor.
        if (performance.now() - lastCommitAtRef.current < 200) {
            return true;
        }
        if (checkExistingLabelClick(event))
            return true;
        return placeLabelAtScreen(event.position) !== null;
    }, [isActive, dragging, checkExistingLabelClick, placeLabelAtScreen]);
    const handleMapDoubleClick = useCallback((event) => {
        if (!viewer || viewer.isDestroyed())
            return false;
        const picked = viewer.scene.pick(event.position);
        if (!(picked === null || picked === void 0 ? void 0 : picked.id))
            return false;
        const screen = { x: event.position.x, y: event.position.y };
        for (const [labelId, label] of labels.entries()) {
            if (picked.id === label.entity) {
                setEditingLabel({ labelId, screenPosition: screen });
                return true;
            }
        }
        for (const [savedId, savedLabel] of savedLabelsRef.current.entries()) {
            if (picked.id === savedLabel.entity) {
                hydrateSavedLabel(savedLabel, resolveServerFolderIdRef.current(savedId), screen, 'edit');
                return true;
            }
        }
        return false;
    }, [viewer, labels, hydrateSavedLabel]);
    const handleMapHover = useCallback((event) => {
        if (!viewer || viewer.isDestroyed())
            return false;
        const cur = draggingRef.current;
        if (!cur)
            return false;
        const position = pickTerrainPositionRef.current(event.endPosition);
        if (!position || !isPositionInConstraintRef.current(position))
            return true;
        const label = labelsRef.current.get(cur.labelId);
        if (!label)
            return true;
        if (label.entity) {
            label.entity.position = new Cesium.ConstantPositionProperty(position);
        }
        setLabels(prev => {
            const next = new Map(prev);
            next.set(cur.labelId, { ...label, position });
            return next;
        });
        return true;
    }, [viewer]);
    const commitEditing = useCallback((text) => {
        var _a;
        const current = editingRef.current;
        if (!current) {
            lastCommitAtRef.current = performance.now();
            return;
        }
        const label = labelsRef.current.get(current.labelId);
        if (!label) {
            setEditingLabel(null);
            lastCommitAtRef.current = performance.now();
            return;
        }
        const trimmed = text.trim();
        if (trimmed.length === 0) {
            if (label.text.length === 0 && !label.serverArtifactId) {
                // Brand-new local label with no text yet — empty commit discards it.
                if (label.entity && dataSourceRef.current) {
                    dataSourceRef.current.entities.remove(label.entity);
                }
                setLabels(prev => {
                    const next = new Map(prev);
                    next.delete(current.labelId);
                    return next;
                });
                setEditingLabel(null);
                lastCommitAtRef.current = performance.now();
                return;
            }
            // Existing label (saved or unsaved-with-text): empty commit
            // closes the editor without changing anything. Server data, if
            // any, is left intact.
            setEditingLabel(null);
            lastCommitAtRef.current = performance.now();
            return;
        }
        const isFirstCommit = label.text.length === 0 && !label.serverArtifactId;
        setLabels(prev => {
            const next = new Map(prev);
            next.set(current.labelId, { ...label, text: trimmed, name: trimmed });
            return next;
        });
        setEditingLabel(null);
        lastCommitAtRef.current = performance.now();
        if (isFirstCommit) {
            setSelectedLabel(current);
            (_a = onFirstCommitRef.current) === null || _a === void 0 ? void 0 : _a.call(onFirstCommitRef);
        }
        else if (label.serverArtifactId) {
            // After editing a hydrated saved label, keep the pop-out open on
            // it so the user can hit "Save changes" — otherwise the local
            // edit looks like it might not be persistable.
            setSelectedLabel(current);
        }
    }, []);
    const cancelEditing = useCallback(() => {
        const current = editingRef.current;
        if (!current) {
            lastCommitAtRef.current = performance.now();
            return;
        }
        const label = labelsRef.current.get(current.labelId);
        if (!label) {
            setEditingLabel(null);
            lastCommitAtRef.current = performance.now();
            return;
        }
        if (label.text.length === 0 && !label.serverArtifactId) {
            // Brand-new local label, escaped before any text — discard.
            if (label.entity && dataSourceRef.current) {
                dataSourceRef.current.entities.remove(label.entity);
            }
            setLabels(prev => {
                const next = new Map(prev);
                next.delete(current.labelId);
                return next;
            });
        }
        setEditingLabel(null);
        lastCommitAtRef.current = performance.now();
    }, []);
    const updateLabelStyle = useCallback((id, patch) => {
        const label = labelsRef.current.get(id);
        if (!label)
            return;
        setLabels(prev => {
            const map = new Map(prev);
            map.set(id, { ...label, ...patch });
            return map;
        });
    }, []);
    const updateLabelName = useCallback((id, name) => {
        const label = labelsRef.current.get(id);
        if (!label)
            return;
        setLabels(prev => {
            const map = new Map(prev);
            map.set(id, { ...label, name });
            return map;
        });
    }, []);
    const removeLabel = useCallback((id) => {
        var _a;
        const label = labelsRef.current.get(id);
        if (!label)
            return;
        if (label.entity && dataSourceRef.current) {
            dataSourceRef.current.entities.remove(label.entity);
        }
        setLabels(prev => {
            const map = new Map(prev);
            map.delete(id);
            return map;
        });
        if ((selectedLabel === null || selectedLabel === void 0 ? void 0 : selectedLabel.labelId) === id)
            setSelectedLabel(null);
        if (((_a = editingRef.current) === null || _a === void 0 ? void 0 : _a.labelId) === id)
            setEditingLabel(null);
    }, [selectedLabel]);
    const dismissSelection = useCallback(() => setSelectedLabel(null), []);
    const updateLabelOpacity = useCallback((id, opacity) => {
        setItemOpacities(prev => {
            const next = new Map(prev);
            next.set(id, opacity);
            return next;
        });
    }, []);
    const getItemOpacity = (id) => { var _a; return (_a = itemOpacities.get(id)) !== null && _a !== void 0 ? _a : 1; };
    const beginEditExisting = useCallback((labelId) => {
        const label = labelsRef.current.get(labelId);
        if (!label || !viewer)
            return;
        const screen = screenPositionForEntity(viewer, label.entity);
        if (!screen)
            return;
        setEditingLabel({ labelId, screenPosition: screen });
    }, [viewer]);
    // Id-based actions. The DOM overlay calls these directly so the
    // visible text becomes the hit target — the Cesium picker entity is
    // too narrow for typical multi-character labels.
    const selectLabelById = useCallback((labelId) => {
        var _a, _b;
        if (!viewer || viewer.isDestroyed())
            return;
        const local = labelsRef.current.get(labelId);
        if (local) {
            const screen = (_a = screenPositionForEntity(viewer, local.entity)) !== null && _a !== void 0 ? _a : { x: 0, y: 0 };
            setSelectedLabel({ labelId, screenPosition: screen });
            return;
        }
        const saved = savedLabelsRef.current.get(labelId);
        if (saved) {
            const screen = (_b = screenPositionForEntity(viewer, saved.entity)) !== null && _b !== void 0 ? _b : { x: 0, y: 0 };
            hydrateSavedLabel(saved, resolveServerFolderIdRef.current(labelId), screen, 'select');
        }
    }, [viewer, hydrateSavedLabel]);
    const beginEditByLabelId = useCallback((labelId) => {
        var _a, _b;
        if (!viewer || viewer.isDestroyed())
            return;
        const local = labelsRef.current.get(labelId);
        if (local) {
            const screen = (_a = screenPositionForEntity(viewer, local.entity)) !== null && _a !== void 0 ? _a : { x: 0, y: 0 };
            setEditingLabel({ labelId, screenPosition: screen });
            return;
        }
        const saved = savedLabelsRef.current.get(labelId);
        if (saved) {
            const screen = (_b = screenPositionForEntity(viewer, saved.entity)) !== null && _b !== void 0 ? _b : { x: 0, y: 0 };
            hydrateSavedLabel(saved, resolveServerFolderIdRef.current(labelId), screen, 'edit');
        }
    }, [viewer, hydrateSavedLabel]);
    const beginDragByLabelId = useCallback((labelId, _event) => {
        var _a;
        if (!viewer || viewer.isDestroyed())
            return;
        // For saved labels the editable copy may not exist yet — hydrate
        // so position updates have a home in the labels Map.
        let dragId = labelId;
        if (!labelsRef.current.has(labelId)) {
            const saved = savedLabelsRef.current.get(labelId);
            if (!saved)
                return;
            const screen = (_a = screenPositionForEntity(viewer, saved.entity)) !== null && _a !== void 0 ? _a : { x: 0, y: 0 };
            const result = hydrateSavedLabel(saved, resolveServerFolderIdRef.current(labelId), screen, 'select');
            if (!result)
                return;
            dragId = result;
        }
        setDragging({ labelId: dragId });
        disableCameraControls(viewer);
        const canvas = viewer.canvas;
        const onMove = (ev) => {
            if (!viewer || viewer.isDestroyed())
                return;
            const rect = canvas.getBoundingClientRect();
            const pt = new Cesium.Cartesian2(ev.clientX - rect.left, ev.clientY - rect.top);
            const position = pickTerrainPositionRef.current(pt);
            if (!position)
                return;
            if (!isPositionInConstraintRef.current(position))
                return;
            const label = labelsRef.current.get(dragId);
            if (!label)
                return;
            if (label.entity) {
                label.entity.position = new Cesium.ConstantPositionProperty(position);
            }
            setLabels(prev => {
                const next = new Map(prev);
                next.set(dragId, { ...label, position });
                return next;
            });
        };
        const onUp = () => {
            if (viewer && !viewer.isDestroyed())
                enableCameraControls(viewer);
            setDragging(null);
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, [viewer, hydrateSavedLabel]);
    const clearAllLabels = useCallback(() => {
        if (dataSourceRef.current) {
            labelsRef.current.forEach(l => {
                if (l.entity)
                    dataSourceRef.current.entities.remove(l.entity);
            });
        }
        setLabels(new Map());
        setEditingLabel(null);
        setSelectedLabel(null);
        setLabelCounter(1);
    }, []);
    const isEntityDraggable = useCallback((entity) => {
        for (const [, label] of labelsRef.current.entries()) {
            if (entity === label.entity)
                return true;
        }
        return false;
    }, []);
    return {
        labels,
        editingLabel,
        selectedLabel,
        dragging,
        isVisible,
        setIsVisible,
        handleMapClick,
        handleMapHover,
        handleMapMouseDown,
        handleMapMouseUp,
        handleMapDoubleClick,
        checkExistingLabelClick,
        isEntityDraggable,
        commitEditing,
        cancelEditing,
        beginEditExisting,
        selectLabelById,
        beginEditByLabelId,
        beginDragByLabelId,
        hydrateSavedLabel,
        updateLabelStyle,
        updateLabelName,
        updateLabelOpacity,
        getItemOpacity,
        removeLabel,
        dismissSelection,
        clearAllLabels,
        hideAll,
        showAll,
        toggleItemVisibility,
        isItemVisible,
        labelCount: labels.size
    };
};
